import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFormatMessage } from '../../intl/intlHooks';

import { getTotalValue } from '../../lineItem/lineItemSelectors';

import { useUsers } from '../UserContext';
import {userMessages} from "../userMessages";

const getFontSize = remainingAmount => {
    const size = remainingAmount.toString().split('').length;
    if (size < 4) {
        return '2rem';
    }
    if (size >= 4 && size < 6) {
        return '1.6rem';
    }
    return '1.2rem';
};

const getPercentage = (remainingAmount, availableAmount) => ((remainingAmount / availableAmount) * 100);

export const AmountCircle = ({ availableAmount, remainingAmount, currency, pointAvailableLabel, pointsRemainingLabel }) => {
    const formatMessage = useFormatMessage();
    const getText = (available, remaining) => formatMessage((available === remaining ? pointAvailableLabel : pointsRemainingLabel));
    const percentage = getPercentage(remainingAmount, availableAmount);
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 0
    });

    return (
        <div className="amount-circle">
            <div className="amount-circle__circle">
                <svg className="circle" viewBox="0 0 33.83098862 33.83098862">
                    <circle
                        className="circle__secondary"
                        strokeWidth="2"
                        fill="none"
                        cx="16.91549431"
                        cy="16.91549431"
                        r="15.91549431"
                    />

                    <circle
                        className="circle__primary"
                        strokeWidth="2"
                        strokeDasharray={`${percentage},100`}
                        strokeLinecap="butt"
                        fill="none"
                        cx="16.91549431"
                        cy="16.91549431"
                        r="15.91549431"
                    />
                </svg>
            </div>
            <div style={{ fontSize: getFontSize(remainingAmount) }} className="amount-circle__value">
                {formatter.format(remainingAmount)}
            </div>
            <div className="amount-circle__text">{getText(remainingAmount, availableAmount)}</div>
        </div>
    );
};

AmountCircle.defaultProps = {
    availableAmount: 0,
    remainingAmount: 0,
    currency: "USD",
};

AmountCircle.propTypes = {
    availableAmount: PropTypes.number,
    remainingAmount: PropTypes.number,
    currency: PropTypes.string,
    pointAvailableLabel: PropTypes.object.isRequired,
    pointsRemainingLabel: PropTypes.object.isRequired,
};

export const InnerRing = ({ totalValue }) => {
    const formatMessage = useFormatMessage();
    const { userData, setAmountUsed, fetching, fetchError } = useUsers();

    useEffect(() => {
        setAmountUsed(totalValue);
    }, [totalValue, setAmountUsed]);

    return userData && (
        <Fragment>
            {fetching && (<span>{formatMessage(userMessages.userLoading)}</span>)}
            {fetchError && (<span>{formatMessage(userMessages.userNotLoggedIn)}</span>)}
            {
                !fetchError && !fetching && (
                    <AmountCircle
                        availableAmount={Number(userData.availableAmount)}
                        remainingAmount={userData.availableAmount - totalValue}
                        currency={userData.currency}
                        pointAvailableLabel={userMessages.pointsAvailable}
                        pointsRemainingLabel={userMessages.pointsRemaining}
                    />
                )
            }
        </Fragment>
    )
};

InnerRing.defaultProps = {
    totalValue: 0,
};

InnerRing.propTypes = {
    totalValue: PropTypes.number,
};

const mapStateToProps = state => ({
    totalValue: getTotalValue(state)
});

export const PointsRing = connect(mapStateToProps)(InnerRing);
